import HandoffPatientInfo from "@/components/popups/patient_handoff/HandoffPatientInfo";
import Util, {
  Colors,
  DATE_FORMAT,
  LevelRange
} from "@/components/shared/util";
import { parseServerTime } from "@/filters/DateAndTime";
import GraphRanges from "@/mixins/GraphRanges";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { RiskFactor } from "@/store/types";
import _ from "lodash";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { TableData } from "../shared/table/DynamicTable";
import HeartGraphs from "./HeartGraphs";

@Component({
  components: { HandoffPatientInfo, HeartGraphs }
})
export default class PatientHandoff extends mixins(GraphRanges) {
  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("graph", patientNamespace)
  graph!: any;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("infusions", patientNamespace)
  infusions!: any;

  @Getter("now", patientNamespace)
  now!: any;

  get timingEvents() {
    const events: TableData[] = [];
    if (
      this.config.popups.PatientHandoff.display.or ||
      this.config.popups.PatientHandoff.display.pacu
    ) {
      events.push({
        label: "In Room",
        value: parseServerTime(this.inRoomDate).format(DATE_FORMAT)
      });
      events.push({
        label: "Surgical Incision",
        value: parseServerTime(this.incisionDate).format(DATE_FORMAT)
      });
    }

    return events;
  }

  get inRoomDate(): string {
    const inRoomPriorDate = _.get(
      this,
      "current.body.prior.current.PatientInRoom.time"
    );
    return this.config.popups.PatientHandoff.display.pacu
      ? inRoomPriorDate
      : this.current.body.current.PatientInRoom.time;
  }

  get incisionDate(): string {
    const incisionPriorDate = _.get(
      this,
      "current.body.prior.current.SurgicalIncision.time"
    );
    const incisionPriorValue = _.get(
      this,
      "current.body.prior.current.SurgicalIncision.value"
    );
    return this.config.popups.PatientHandoff.display.pacu
      ? incisionPriorValue === 2
        ? incisionPriorDate
        : ""
      : this.current.body.current.SurgicalIncision.value === 2
      ? this.current.body.current.SurgicalIncision.time
      : "";
  }

  get history() {
    const filteredHistory = this.vars.riskFactor
      .filter((factor: RiskFactor) => {
        return (
          factor.type === "handp" &&
          this.current.body.risk.includes(factor.name) &&
          factor.part !== "ett" &&
          factor.segment !== "admit"
        );
      })
      .map((factor: RiskFactor) => {
        return factor.display;
      });
    if (filteredHistory.length === 0) {
      filteredHistory.push("None found");
    }
    return filteredHistory;
  }

  get airway() {
    const airway = this.vars.riskFactor
      .filter((factor: RiskFactor) => {
        return (
          this.current.body.risk.includes(factor.name) &&
          factor.part === "ett" &&
          factor.weight !== 0
        );
      })
      .map((factor: RiskFactor) => {
        return factor.display;
      });
    return airway;
  }

  get allergies() {
    const allergies = _.get(
      this.current,
      "body.current.Allergies.text",
      "No documented allergies"
    ).trim();
    return allergies.split("|");
  }

  get labs() {
    const atoms = _.get(this.current, "body.atoms");
    if (!atoms) {
      return [];
    }

    let expectedLabs = [
      { name: "ACT", value: atoms.act },
      { name: "Albumin", value: atoms.albumin },
      { name: "ALP", value: atoms.alp },
      { name: "ALT", value: atoms.alt },
      { name: "Amylase", value: atoms.amylase },

      { name: "Calcium", value: atoms.calcium },

      { name: "CrCl", value: atoms.crCl },
      {
        name: "Creatinine",
        value: atoms.cr
      },
      { name: "GGT", value: atoms.ggt },
      { name: "Glucose", value: atoms.glucose },

      {
        name: atoms.hct.legend.startsWith("Hct") ? "HCT" : "HGB",
        value: atoms.hct
      },
      { name: "INR", value: atoms.inr },
      { name: "K+", value: atoms.pot },
      { name: "Lactate", value: atoms.lactate },
      { name: "Lipase", value: atoms.lipase },

      { name: "Platelet", value: atoms.platelet },
      { name: "SGOT", value: atoms.sgot },
      { name: "Sodium", value: atoms.sodium },

      { name: "Base Excess", value: atoms.abgBE },
      { name: "HCO<sub>3</sub>", value: atoms.bicarb },
      { name: "Calcium (Art)", value: atoms.abgCalcium },
      { name: "FiO<sub>2</sub>", value: atoms.abgFiO2 },
      { name: "O<sub>2</sub> Sat", value: atoms.abgO2Sat },
      { name: "PaCO<sub>2</sub>", value: atoms.abgPaCO2 },
      { name: "PaO<sub>2</sub>", value: atoms.abgPaO2 },

      { name: "pH", value: atoms.abgpH }
    ];

    const list = expectedLabs
      .filter(lab => {
        return lab !== undefined && lab.value !== undefined;
      })
      .map(lab => {
        let value = "N/A";
        if (lab.value && lab.value.legend) {
          value = lab.value.legend.replace("|", " ");

          // Remove Prefix x from x = y to be just y
          const i = value.indexOf("=");
          if (i > 0) {
            value = value.substr(i + 1).trim();
          }
        }
        return {
          name: lab.name,
          value,
          backgroundColor: this.makeBackgroundColor(lab.value.level)
        };
      });
    return list.map(l => {
      return {
        label: l.name,
        value: l.value.replace("Hct", "").replace("Hgb", ""),
        backgroundColor: l.backgroundColor,
        textColor:
          l.backgroundColor === Colors.Red ? Colors.White : Colors.Black
      };
    });
  }

  makeBackgroundColor(level: LevelRange) {
    const color = Util.getLevelColor(level, false);
    return color === Colors.Red || color === Colors.Yellow ? color : "";
  }

  get mappedInfusions() {
    const mappedInfusions = _.flatten(Object.values(this.infusions)).map(
      (infusion: any) => {
        return infusion.label;
      }
    );
    if (mappedInfusions.length === 0) {
      mappedInfusions.push("None found");
    }
    return mappedInfusions;
  }

  get antiEmetics() {
    const list = _.get(this.current, "body.current.AntiEmetics.text", "").split(
      ","
    );
    return list;
  }

  // null to avoid filtering by active or not, and only return message
  filterAlerts(alerts: any, active: boolean | null = true) {
    const filteredAlerts = Object.values(alerts)
      .filter((alert: any) => {
        const rules = this.vars.alertRules;
        const alertRule = rules[alert.alertIndex];
        if (active === null) {
          return true;
        }
        return (
          alert.active === active && alertRule.icon.substr(0, 9) === "alertPhys"
        );
      })
      .map((alert: any) => {
        const value = `${alert.message.split("^^")[0]} [count = ${
          alert.count
        }]`;
        if (active === null) {
          return { value };
        }
        const alertRule = this.vars.alertRules[alert.alertIndex];
        const backgroundColor = Util.getLevelColor(alertRule.level, false);
        const textColor =
          backgroundColor === Colors.Red ? Colors.White : Colors.Black;
        return { value, backgroundColor, textColor };
      });
    if (filteredAlerts.length === 0) {
      filteredAlerts.push({ value: "None" });
    }
    return filteredAlerts;
  }

  get ioTable() {
    return [
      {
        label: "EBL",
        value: this.current.body.atoms.ebl.value
      },
      {
        label: "Urine",
        value: this.current.body.atoms.urine.value
      }
    ];
  }

  get activeAlerts() {
    return this.filterAlerts(this.current.body.alertStatus);
  }

  get inactiveAlerts() {
    return this.filterAlerts(this.current.body.alertStatus, false);
  }

  get pacuAlerts() {
    return this.filterAlerts(this.current.body.alertStatus);
  }

  get orAlerts(): any[] {
    const alertStatus = _.get(this, "current.body.prior.alertStatus");
    if (!alertStatus) {
      return [];
    }
    return this.filterAlerts(alertStatus, null);
  }

  get careCoordinationNoteSplit(): string[] {
    return _.get(
      this,
      "current.body.current.CareCoordinationNote.text",
      ""
    ).split("|");
  }

  get miscNoteSplit(): string[] {
    return _.get(this, "current.body.current.MiscNote.text", "").split("|");
  }

  get towerNoteSplit(): string[] {
    return _.get(this, "current.body.current.TowerNote.text", "");
  }
}
